import React, { useContext } from 'react';

import { BiArrowFromTop } from 'react-icons/bi';
import { IoMdCloseCircle } from 'react-icons/io';
import { TbColumns2 } from 'react-icons/tb';
import styled, { ThemeContext } from 'styled-components';

import { Theme } from 'contexts/theme';

import {
    MicrostoreLocationTemplateFragmentFragment,
    ProductArrangementFragmentFragment,
    ProductArrangementReassortmentType,
    ProductFlow,
} from 'data/__generated__';

import {
    MicrostoreLocationFormValues,
    MicrostoreProductStockInfoFlat,
    MicrostoreShelfFormValues,
} from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/FormHelper/DataMapper';

import { Spinner } from 'components/Spinner';

import { BirthdayDate, dateFromString, formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';

function getProductInfoInArrangement({
    productId,
    microstoreLocationTemplate,
    productArrangement,
}: {
    productId: string | null;
    microstoreLocationTemplate: MicrostoreLocationTemplateFragmentFragment | null;
    productArrangement: ProductArrangementFragmentFragment | null;
}): Omit<ProductArrangementFragmentFragment['productIdsWithStockMax'][number], '__typename'> | null {
    if (!productId) {
        return null;
    }

    if (
        microstoreLocationTemplate &&
        microstoreLocationTemplate.reassortmentType &&
        microstoreLocationTemplate.stockMax
    ) {
        return {
            productId,
            stockMax: microstoreLocationTemplate.stockMax ?? 0,
        };
    }

    if (productArrangement) {
        return (
            productArrangement.productIdsWithStockMax.find(
                (productIdWithStockMax) => productId === productIdWithStockMax.productId,
            ) ?? null
        );
    }

    return null;
}

function getStockMaxFromProductInfoInArrangement({
    productInfoInArrangement,
}: {
    productInfoInArrangement: Omit<
        ProductArrangementFragmentFragment['productIdsWithStockMax'][number],
        '__typename'
    > | null;
}): number | null {
    return productInfoInArrangement?.stockMax ?? null;
}

function getExpiryDateColor(expiryDate: string, theme: Theme) {
    const today = new Date();
    if (formatDateAsAnniversary({ dateTime: today }) === expiryDate) {
        return theme.warningColor;
    }

    if (today > dateFromString(expiryDate)) {
        return theme.errorColor;
    }

    return 'inherit';
}

function getExpiryDateDisplay(
    expiryDateRecords: MicrostoreProductStockInfoFlat['expiryDateRecordsWarehouse'],
    theme: any,
) {
    if (!expiryDateRecords.length) {
        return {
            label: null,
            color: null,
        };
    }
    const sortedExpiryDateRecords = [...expiryDateRecords].sort((expiryDateRecordA, expiryDateRecordB) =>
        dateFromString(expiryDateRecordA.expiryDate) < dateFromString(expiryDateRecordB.expiryDate) ? -1 : 1,
    );

    return {
        label:
            formatDateForDisplay(sortedExpiryDateRecords[0].expiryDate) +
            (sortedExpiryDateRecords.length > 1 ? '...' : ''),
        color: getExpiryDateColor(sortedExpiryDateRecords[0].expiryDate, theme),
    };
}

function getLocationBackgroundColor({
    allSiteLocations,
    flow,
    location,
    trackExpiryDate,
}: {
    allSiteLocations: MicrostoreLocationFormValues[];
    flow: ProductFlow | undefined;
    location: MicrostoreLocationFormValues;
    trackExpiryDate: boolean;
}) {
    if (location.replacementProductId === null) {
        return 'inherit';
    }

    const isPresentToday = allSiteLocations.some((loc) => location.replacementProductId === loc.productId);
    const isRelocated = location.replacementProductId !== location.productId && isPresentToday;

    if (isRelocated) {
        return '#fffb00c8';
    }

    if (trackExpiryDate && flow !== ProductFlow.Fresh) {
        return '#e181ff60';
    }

    if (location.replacementProductId === location.productId) {
        return 'inherit';
    }

    return '#1bbe4f5a';
}

function getTargetStockDisplay({ targetStock, stockMax }: { targetStock: number | null; stockMax: number | null }): {
    label: string;
    isError: boolean;
} {
    if (stockMax === null) {
        return { label: 'Auto Stock Cible ?', isError: true };
    }
    if (targetStock === null) {
        return { label: `Auto Stock Cible ${stockMax}`, isError: false };
    }
    return { label: `Auto Stock Cible : empl. ${targetStock} · templ. ${stockMax}`, isError: false };
}

export const TomorrowShelf = ({
    allSiteLocations,
    canBeUpdated = true,
    earliestDLCDateToKeepForStockCalculation,
    isSecured,
    isToday,
    microstoreProductsStockInfo,
    getMicrostoreProductsStockInfoLoading,
    nextSiteDeliveryDate,
    removalDate,
    selectedLocationPosition,
    selectedShelfIndex,
    setIsToday,
    setSelectedLocationPosition,
    setSelectedShelfIndex,
    shelf,
    shelfIndex,
    rowMinWidthPx,
    updateLocationAt,
    updateSelectedLocation,
}: {
    allSiteLocations: MicrostoreLocationFormValues[];
    canBeUpdated?: boolean;
    earliestDLCDateToKeepForStockCalculation: BirthdayDate | null;
    isSecured: boolean;
    isToday: boolean;
    microstoreProductsStockInfo: MicrostoreProductStockInfoFlat[] | null;
    getMicrostoreProductsStockInfoLoading?: boolean;
    nextSiteDeliveryDate: BirthdayDate | null;
    removalDate: string | null;
    selectedLocationPosition: { column: number; row: number } | null;
    selectedShelfIndex: number | null;
    setIsToday: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedLocationPosition: React.Dispatch<React.SetStateAction<{ column: number; row: number } | null>>;
    setSelectedShelfIndex: React.Dispatch<React.SetStateAction<number | null>>;
    shelf: MicrostoreShelfFormValues;
    shelfIndex: number;
    rowMinWidthPx: number;
    updateLocationAt?: (args: {
        shelfIndex: number;
        row: number;
        column: number;
        values: Partial<MicrostoreLocationFormValues>;
    }) => void;
    updateSelectedLocation: (values: Partial<MicrostoreLocationFormValues>) => void;
}) => {
    const theme = useContext(ThemeContext);

    const canSelectLocation =
        canBeUpdated && (!removalDate || (!!nextSiteDeliveryDate && nextSiteDeliveryDate < removalDate));

    return (
        <Shelf>
            <ShelfHeader>J+1</ShelfHeader>
            <ShelfBody>
                {shelf.locationTable.map((locationRow, locationRowIndex) => {
                    return (
                        <LocationRow key={locationRowIndex} $minWidthPx={rowMinWidthPx}>
                            {locationRow.map((location, locationColumnIndex) => {
                                const { microstoreLocationTemplate, replacementProductId, productId, targetStock } =
                                    location;

                                const replacementStockInfo = microstoreProductsStockInfo?.find(
                                    ({ _id }) => _id === replacementProductId,
                                );

                                // The replacementProduct fragment can be stale w.r.t. replacementProductId after an
                                // optimistic form edit (the id updates immediately, but `replacementProduct` only
                                // refreshes on save/reload). Fall back to the stock-info entry, which is always keyed
                                // on the current replacementProductId, so the slot renders right away.
                                const replacementProduct =
                                    replacementProductId && location.replacementProduct?._id === replacementProductId
                                        ? location.replacementProduct
                                        : (replacementStockInfo ?? null);

                                const { label: expiryDateLabel, color: expiryDateColor } = getExpiryDateDisplay(
                                    replacementStockInfo?.expiryDateRecordsWarehouse ?? [],
                                    theme,
                                );
                                const locationBackgroundColor = getLocationBackgroundColor({
                                    allSiteLocations,
                                    flow: replacementProduct?.flow,
                                    location,
                                    trackExpiryDate: !!replacementProduct?.trackExpiryDate,
                                });

                                const locationsWithSameReplacement = allSiteLocations.filter(
                                    (loc) => loc.replacementProductId === replacementProductId,
                                );
                                const replacementProductStockInSite = Math.max(
                                    0,
                                    replacementStockInfo?.stockOnSite ?? 0,
                                );
                                const replacementProductStockInLocation = (() => {
                                    const idx = locationsWithSameReplacement.findIndex(
                                        ({ _id }) => _id === location._id,
                                    );
                                    if (idx === -1) return 0;
                                    return (
                                        Math.floor(
                                            replacementProductStockInSite / (locationsWithSameReplacement.length || 1),
                                        ) +
                                        (idx < replacementProductStockInSite % locationsWithSameReplacement.length
                                            ? 1
                                            : 0)
                                    );
                                })();

                                const stockToRemoveInLocation = (() => {
                                    const todayProductLocation = allSiteLocations.find(
                                        ({ productId: pid }) => pid === replacementProductId,
                                    );
                                    if (!todayProductLocation) {
                                        return 0;
                                    }
                                    const stockToKeep = todayProductLocation.expiryDates.reduce(
                                        (acc, { expiryDate, quantity }) =>
                                            acc +
                                            (!!earliestDLCDateToKeepForStockCalculation &&
                                            expiryDate >= earliestDLCDateToKeepForStockCalculation
                                                ? quantity
                                                : 0),
                                        0,
                                    );
                                    const quantitiesWithDLC = todayProductLocation.expiryDates.reduce(
                                        (acc, { quantity }) => acc + quantity,
                                        0,
                                    );
                                    const quantitiesMissingInDLC = Math.max(
                                        replacementProductStockInSite - quantitiesWithDLC,
                                        0,
                                    );
                                    const stockToRemove = Math.max(
                                        replacementProductStockInSite - stockToKeep - quantitiesMissingInDLC,
                                        0,
                                    );
                                    const idx = locationsWithSameReplacement.findIndex(
                                        ({ _id }) => _id === location._id,
                                    );
                                    if (idx === -1) return 0;
                                    return (
                                        Math.floor(stockToRemove / (locationsWithSameReplacement.length || 1)) +
                                        (idx < stockToRemove % locationsWithSameReplacement.length ? 1 : 0)
                                    );
                                })();

                                const isLocationSelected =
                                    canBeUpdated &&
                                    shelfIndex === selectedShelfIndex &&
                                    locationColumnIndex === selectedLocationPosition?.column &&
                                    locationRowIndex === selectedLocationPosition?.row &&
                                    !isToday;

                                const stockUsedInMicrostore = locationsWithSameReplacement.reduce(
                                    (sum, loc) => sum + loc.stockToAdd,
                                    0,
                                );
                                const stockAvailableForReassortment = replacementStockInfo
                                    ? replacementStockInfo.stockAvailableWarehouse -
                                      stockUsedInMicrostore -
                                      replacementStockInfo.stockUsedInOtherMicrostores
                                    : 0;

                                const usedReassortmentType = microstoreLocationTemplate.productArrangement
                                    ? microstoreLocationTemplate.productArrangement.reassortmentType
                                    : microstoreLocationTemplate.reassortmentType;

                                const productInfoInArrangement = getProductInfoInArrangement({
                                    microstoreLocationTemplate,
                                    productArrangement: microstoreLocationTemplate.productArrangement || null,
                                    productId: productId || null,
                                });
                                const replacementProductStockMax =
                                    getStockMaxFromProductInfoInArrangement({
                                        productInfoInArrangement,
                                    }) ?? 0;
                                const targetStockDisplay = getTargetStockDisplay({
                                    targetStock: targetStock ?? null,
                                    stockMax: getStockMaxFromProductInfoInArrangement({ productInfoInArrangement }),
                                });

                                const isDoubleCoil = replacementProduct?.isDoubleCoil;

                                return (
                                    <Location
                                        key={locationColumnIndex}
                                        $canSelectLocation={canSelectLocation}
                                        $isSelected={isLocationSelected}
                                        $hasStockToAdd={!!location.stockToAdd}
                                        $isDoubleCoil={isDoubleCoil}
                                        onClick={(event) => {
                                            if (!canSelectLocation) {
                                                return;
                                            }
                                            event.stopPropagation();
                                            setSelectedShelfIndex(shelfIndex);
                                            setSelectedLocationPosition({
                                                column: locationColumnIndex,
                                                row: locationRowIndex,
                                            });
                                            setIsToday(false);
                                        }}
                                        $backgroundColor={locationBackgroundColor}
                                    >
                                        {replacementProduct ? (
                                            <>
                                                <LocationProductName>
                                                    {isDoubleCoil ? (
                                                        <DoubleCoilBadge title="Double spire">
                                                            <TbColumns2 size={14} />
                                                        </DoubleCoilBadge>
                                                    ) : null}
                                                    {replacementProduct.name}
                                                </LocationProductName>
                                                {expiryDateLabel ? (
                                                    <LocationProductInfoRow $hideOnPrint>
                                                        <LocationProductInfo color={expiryDateColor}>
                                                            {expiryDateLabel}
                                                        </LocationProductInfo>
                                                    </LocationProductInfoRow>
                                                ) : null}

                                                {!replacementProduct.isWithoutStock ? (
                                                    <>
                                                        <LocationProductInfoRow $hideOnPrint>
                                                            {getMicrostoreProductsStockInfoLoading ? (
                                                                <Spinner size={12} />
                                                            ) : (
                                                                <>
                                                                    <LocationProductInfo
                                                                        color={
                                                                            stockAvailableForReassortment > 0
                                                                                ? 'inherit'
                                                                                : theme?.errorColor
                                                                        }
                                                                    >
                                                                        Disp&nbsp;
                                                                        {stockAvailableForReassortment}
                                                                    </LocationProductInfo>
                                                                    {stockToRemoveInLocation !== 0 ? (
                                                                        <>
                                                                            <LocationProductInfo>
                                                                                {' '}
                                                                                |{' '}
                                                                            </LocationProductInfo>
                                                                            <LocationProductInfo
                                                                                color={theme?.errorColor}
                                                                            >
                                                                                S-:&nbsp;{stockToRemoveInLocation}
                                                                            </LocationProductInfo>
                                                                        </>
                                                                    ) : null}
                                                                    <LocationProductInfo> | </LocationProductInfo>
                                                                    <LocationProductInfo>
                                                                        Liv&nbsp;+{location.stockToAdd}
                                                                    </LocationProductInfo>
                                                                    <LocationProductInfo> | </LocationProductInfo>
                                                                    <LocationProductInfo
                                                                        $isBold
                                                                        color={
                                                                            replacementProductStockInLocation +
                                                                                location.stockToAdd >
                                                                            0
                                                                                ? 'inherit'
                                                                                : theme?.errorColor
                                                                        }
                                                                    >
                                                                        Fin&nbsp;
                                                                        {replacementProductStockInLocation +
                                                                            location.stockToAdd -
                                                                            stockToRemoveInLocation}
                                                                    </LocationProductInfo>
                                                                </>
                                                            )}
                                                        </LocationProductInfoRow>
                                                        <LocationProductInfoRow $hideOnWeb>
                                                            <LocationProductInfo $isBold>
                                                                ST:&nbsp;
                                                                {replacementProductStockInLocation +
                                                                    location.stockToAdd -
                                                                    stockToRemoveInLocation}
                                                            </LocationProductInfo>
                                                        </LocationProductInfoRow>
                                                    </>
                                                ) : null}
                                            </>
                                        ) : productId && isLocationSelected ? (
                                            <LocationProductInfoRow
                                                $hideOnPrint
                                                onClick={() =>
                                                    updateSelectedLocation({
                                                        replacementProductId: productId,
                                                        stockToAdd: 0,
                                                    })
                                                }
                                            >
                                                <BiArrowFromTop size={20} />
                                            </LocationProductInfoRow>
                                        ) : null}

                                        {!replacementProduct?.isWithoutStock ? (
                                            <LocationProductInfoRow $hideOnPrint>
                                                {usedReassortmentType === ProductArrangementReassortmentType.Target ? (
                                                    <LocationProductInfo
                                                        color={
                                                            targetStockDisplay.isError ? theme?.errorColor : undefined
                                                        }
                                                    >
                                                        {targetStockDisplay.label}
                                                    </LocationProductInfo>
                                                ) : (
                                                    <LocationProductInfo>Manuel</LocationProductInfo>
                                                )}
                                            </LocationProductInfoRow>
                                        ) : null}
                                        {canSelectLocation && updateLocationAt && location.replacementProductId ? (
                                            <DeleteButton
                                                type="button"
                                                title="Supprimer le produit J+1"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    updateLocationAt({
                                                        shelfIndex,
                                                        row: locationRowIndex,
                                                        column: locationColumnIndex,
                                                        values: {
                                                            replacementProductId: null,
                                                            stockToAdd: 0,
                                                        },
                                                    });
                                                }}
                                            >
                                                <IoMdCloseCircle size={14} color={theme?.errorColor} />
                                            </DeleteButton>
                                        ) : null}
                                    </Location>
                                );
                            })}
                        </LocationRow>
                    );
                })}
            </ShelfBody>
        </Shelf>
    );
};

const Shelf = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1 0 0;
    background-color: ${({ theme }) => theme.darkBackgroundColor};
`;

const ShelfHeader = styled.div`
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};

    @media print {
        font-size: 17px;
    }
`;

const ShelfBody = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 0 0;
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 2px;

    & > :not(:first-child) {
        margin-top: 2px;
    }

    @media print {
        & > :not(:first-child) {
            margin-top: 1px;
        }
    }
`;

const LocationRow = styled.div<{ $minWidthPx: number }>`
    display: flex;
    flex-direction: row;
    flex: 1 0 0;
    min-width: ${({ $minWidthPx }) => $minWidthPx}px;

    & > :not(:first-child) {
        margin-left: 2px;
    }

    @media print {
        min-width: 0;

        & > :not(:first-child) {
            margin-left: 1px;
        }
    }
`;

const Location = styled.div<{
    $canSelectLocation: boolean;
    $isSelected: boolean;
    $backgroundColor: string;
    $hasStockToAdd?: boolean;
    $isDoubleCoil?: boolean;
}>`
    position: relative;
    cursor: ${({ $canSelectLocation }) => ($canSelectLocation ? 'pointer' : 'not-allowed')};
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 0 0' : '1 0 0')};
    min-width: 0;
    border: 1px solid ${({ theme, $isSelected }) => ($isSelected ? theme.ctaPrimaryColor : theme.lightBorderColor)};
    box-shadow: ${({ theme, $isSelected }) => ($isSelected ? theme.boxShadow : 'none')};
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 5px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;

    & > :not(:first-child) {
        margin-top: 5px;
    }

    @media print {
        padding: 1px;
        border: 1px solid
            ${({ theme, $hasStockToAdd }) => ($hasStockToAdd ? theme.successColor : theme.lightBorderColor)};

        & > :not(:first-child) {
            margin-top: 1px;
        }
    }
`;

const DeleteButton = styled.button`
    position: absolute;
    top: 2px;
    right: 2px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    z-index: 1;

    &:hover {
        opacity: 0.7;
    }

    @media print {
        display: none;
    }
`;

const LocationProductName = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-grow: 1;
    font-size: 13px;

    @media print {
        font-size: 9px;
    }
`;

const DoubleCoilBadge = styled.span`
    display: inline-flex;
    align-items: center;
    margin-right: 4px;
    cursor: help;
`;

const LocationProductInfoRow = styled.div<{ $hideOnPrint?: boolean; $hideOnWeb?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    text-align: center;
    display: ${({ $hideOnWeb }) => ($hideOnWeb ? 'none' : 'initial')};

    & > :not(:first-child) {
        margin-left: 2px;
    }

    @media print {
        display: ${({ $hideOnPrint }) => ($hideOnPrint ? 'none' : 'initial')};
    }
`;

const LocationProductInfo = styled.span<{ $hideOnPrint?: boolean; $isBold?: boolean }>`
    text-align: center;
    font-size: 13px;
    color: ${({ color }) => color ?? 'inherit'};
    font-weight: ${({ $isBold }) => ($isBold ? 800 : 500)};

    @media print {
        display: ${({ $hideOnPrint }) => ($hideOnPrint ? 'none' : 'initial')};
        font-size: 9px;
    }
`;
